<template>
  <div class="ds-captain-picker">
    <v-text-field
      v-if="manual"
      :value="value"
      type="number"
      min="1"
      :label="$t('finance.reports2.driverStatement.captainId')"
      :hint="manualReason || $t('finance.reports2.driverStatement.captainIdHint')"
      persistent-hint
      clearable
      class="fin-field"
      @input="onManualInput"
    ></v-text-field>
    <v-autocomplete
      v-else
      :value="value"
      :items="items"
      :loading="loading"
      :search-input.sync="search"
      :label="$t('finance.reports2.driverStatement.captain')"
      :placeholder="$t('finance.reports2.driverStatement.captainHint')"
      :no-data-text="search ? $t('finance.reports2.driverStatement.captainNoResults') : $t('finance.reports2.driverStatement.captainHint')"
      item-value="id"
      item-text="text"
      :filter="keepServerMatch"
      clearable
      hide-details
      class="fin-field fin-field--wide"
      @change="onChange"
    >
      <template slot="item" slot-scope="data">
        <v-list-tile-content>
          <v-list-tile-title>{{ data.item.name }}</v-list-tile-title>
          <v-list-tile-sub-title>
            #{{ data.item.id }} - {{ data.item.username }}
            <span v-if="data.item.glAccountCode" class="ds-picker-code">{{ data.item.glAccountCode }}</span>
          </v-list-tile-sub-title>
        </v-list-tile-content>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
/*
 * Captain picker of report 2.2 (driver statement). Same pattern as
 * ChooseCaptainMulti (GET captain/search?search=..., cache-items), single
 * select, v-model = captain id. captain/search is guarded by DRIVERS (the
 * ledger track widens it to the finance permissions): on a 403 or any
 * failure the picker switches to a plain "driver id" box with a clear
 * message instead of failing silently.
 *
 * The server does the matching (name, phone with or without a prefix, id,
 * GL leaf code), so the autocomplete never filters its rows again: Vuetify's
 * default filter would test the typed text against the item text and drop
 * every row found by a prefixed phone or a leaf code. The item list is
 * managed here (latest server rows + the selected driver), hence no
 * cache-items either: cached rows of an older search would match nothing.
 */
import axios from "axios";

const CancelToken = axios.CancelToken;

export default {
  name: "driver-statement-captain-picker",
  props: {
    value: { type: [Number, String], default: null },
    // {id, first_name, last_name, username} to show for a preset id (deep link / loaded report).
    preset: { type: Object, default: null },
  },
  data() {
    return {
      items: [],
      search: null,
      loading: false,
      manual: false,
      manualReason: "",
      cancel: null,
      timer: null,
    };
  },
  watch: {
    search(val) {
      if (!val || this.manual) {
        return;
      }
      const selected = this.items.find((item) => item.id === this.value);
      if (selected && selected.text === val) {
        return; // the autocomplete echoes the chosen item's text
      }
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.runSearch(val), 300);
    },
    preset: {
      immediate: true,
      handler(captain) {
        if (captain && captain.id) {
          this.remember(captain);
        }
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.timer);
    if (this.cancel) this.cancel();
  },
  methods: {
    normalize(captain) {
      const name = ((captain.first_name || "") + " " + (captain.last_name || "")).trim() || captain.name || "";
      const glAccountCode = captain.gl_account_code || "";
      return {
        id: Number(captain.id),
        name: name,
        username: captain.username || captain.phone || "",
        glAccountCode: glAccountCode,
        text: name + " " + (captain.username || captain.phone || "") + " #" + captain.id + (glAccountCode ? " " + glAccountCode : ""),
        raw: captain,
      };
    },
    keepServerMatch() {
      return true; // every row on the list is a server match (or the selection)
    },
    remember(captain) {
      const item = this.normalize(captain);
      if (!this.items.some((existing) => existing.id === item.id)) {
        this.items = this.items.concat([item]);
      }
    },
    runSearch(val) {
      if (this.cancel) this.cancel();
      this.loading = true;
      this.axios
        .get("captain/search", {
          params: { search: val },
          cancelToken: new CancelToken((c) => {
            this.cancel = c;
          }),
        })
        .then((response) => {
          const list = (response.data && response.data.data) || [];
          const fresh = list.map((captain) => this.normalize(captain));
          const kept = this.items.filter((item) => item.id === Number(this.value));
          const ids = {};
          this.items = kept.concat(fresh).filter((item) => (ids[item.id] ? false : (ids[item.id] = true)));
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return;
          }
          const status = error && error.response ? error.response.status : null;
          const key = status === 403 ? "captainSearchForbidden" : "captainSearchFailed";
          this.manualReason = this.$t("finance.reports2.driverStatement." + key);
          this.manual = true;
          window.getApp.$emit("SHOW_TOAST", this.manualReason, "orange");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onChange(id) {
      const item = this.items.find((entry) => entry.id === id);
      this.$emit("input", id || null);
      this.$emit("change", item ? item.raw : null);
    },
    onManualInput(val) {
      const id = val ? Number(val) : null;
      this.$emit("input", id && id > 0 ? id : null);
      this.$emit("change", null);
    },
  },
};
</script>

<style scoped>
.ds-captain-picker {
  display: inline-block;
  min-width: 260px;
}
.ds-picker-code {
  direction: ltr;
  unicode-bidi: embed;
  margin-inline-start: 6px;
  font-size: 12px;
}
</style>
